<template>
  <section class="cal" aria-label="Calendario de envíos">
    <header class="cal__cabecera">
      <h2 class="cal__titulo">Calendario de envíos</h2>
      <div class="cal__modo" role="group" aria-label="Ver por">
        <button
          type="button"
          class="cal__modo-btn"
          :class="{ 'is-activo': modo === 'ciclo' }"
          :disabled="!ciclo"
          :title="ciclo ? '' : cicloError || 'Cargando ciclo…'"
          :aria-pressed="String(modo === 'ciclo')"
          @click="cambiarModo('ciclo')"
        >Ciclo</button>
        <button
          type="button"
          class="cal__modo-btn"
          :class="{ 'is-activo': modo === 'mes' }"
          :aria-pressed="String(modo === 'mes')"
          @click="cambiarModo('mes')"
        >Mes</button>
      </div>
    </header>

    <div class="cal__rango">
      <template v-if="modo === 'ciclo' && ciclo">
        <span class="cal__rango-nombre">{{ ciclo.nombre }}</span>
        <span class="cal__rango-fechas">{{ corta(ciclo.inicio) }} – {{ corta(ciclo.fin) }}</span>
      </template>
      <template v-else>
        <v-btn icon small aria-label="Mes anterior" @click="moverMes(-1)"><v-icon small>mdi-chevron-left</v-icon></v-btn>
        <span class="cal__rango-nombre cal__rango-nombre--mes">{{ mesTexto }}</span>
        <v-btn icon small aria-label="Mes siguiente" @click="moverMes(1)"><v-icon small>mdi-chevron-right</v-icon></v-btn>
      </template>
    </div>

    <v-progress-linear :active="cargando" indeterminate height="2" color="#00A548"></v-progress-linear>

    <div class="cal__grid" role="grid" :aria-label="modo === 'ciclo' ? 'Semanas del ciclo' : mesTexto">
      <div class="cal__fila cal__fila--dias" role="row">
        <span class="cal__semana" aria-hidden="true"></span>
        <span v-for="d in DIAS" :key="d" class="cal__dia-nombre" role="columnheader">{{ d }}</span>
      </div>
      <div v-for="semana in semanas" :key="semana.inicio" class="cal__fila" role="row">
        <span class="cal__semana" :title="semana.etiqueta ? 'Semana ' + semana.etiqueta.slice(1) + ' del ciclo' : ''">{{ semana.etiqueta }}</span>
        <button
          v-for="celda in semana.dias"
          :key="celda.fecha"
          type="button"
          role="gridcell"
          class="cal__celda"
          :class="{
            'is-fuera': !celda.enRango,
            'is-hoy': celda.fecha === hoy,
            'is-elegido': celda.fecha === dia,
          }"
          :disabled="modo === 'ciclo' && !celda.enRango"
          :aria-label="etiquetaCelda(celda)"
          :aria-selected="String(celda.fecha === dia)"
          @click="dia = celda.fecha"
        >
          <span class="cal__num">{{ celda.num }}</span>
          <span v-if="celda.envios.length" class="cal__puntos" aria-hidden="true">
            <i v-for="e in celda.envios.slice(0, 3)" :key="e.idagenda" :style="{ background: color(e.idplantillas) }"></i>
            <b v-if="celda.envios.length > 3">+{{ celda.envios.length - 3 }}</b>
          </span>
        </button>
      </div>
    </div>

    <div class="cal__detalle">
      <div class="cal__detalle-fecha">
        {{ mayuscula(larga(dia)) }}
        <span v-if="semanaDe(dia)" class="cal__detalle-semana">Semana {{ semanaDe(dia) }}</span>
      </div>

      <ul v-if="enviosDelDia.length" class="cal__lista">
        <li v-for="e in enviosDelDia" :key="e.idagenda" class="cal__envio">
          <i class="cal__marca" :style="{ background: color(e.idplantillas) }" aria-hidden="true"></i>
          <span class="cal__envio-titulo">{{ e.titulo }}</span>
          <v-btn icon x-small :aria-label="'Quitar ' + e.titulo + ' de este día'" :disabled="guardando" @click="quitar(e)">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </li>
      </ul>
      <p v-else class="cal__vacio">Nada agendado este día.</p>

      <div class="cal__agregar">
        <v-autocomplete
          v-model="idplantilla"
          :items="disponibles"
          item-text="titulo"
          item-value="idplantillas"
          label="Plantilla que se envía"
          :no-data-text="plantillas.length ? 'Ya están todas en este día' : 'Primero crea una plantilla en el catálogo'"
          dense
          filled
          hide-details
        ></v-autocomplete>
        <v-btn depressed color="#00A548" class="white--text" :disabled="!idplantilla" :loading="guardando" @click="agendar">
          Agendar
        </v-btn>
      </div>
    </div>

    <div class="cal__resumen">
      <h3 class="cal__resumen-titulo">{{ modo === 'ciclo' ? 'Envíos del ciclo' : 'Envíos de ' + mesTexto }}</h3>
      <p v-if="!resumen.length" class="cal__vacio">
        Aún no hay envíos. Elige un día y agenda la plantilla que toca, empezando por la semana 1.
      </p>
      <div v-for="grupo in resumen" :key="grupo.inicio" class="cal__grupo">
        <div class="cal__grupo-titulo">{{ grupo.titulo }}</div>
        <button
          v-for="e in grupo.envios"
          :key="e.idagenda"
          type="button"
          class="cal__resumen-fila"
          :class="{ 'is-elegido': e.fecha === dia }"
          @click="dia = e.fecha"
        >
          <span class="cal__resumen-fecha">{{ corta(e.fecha, true) }}</span>
          <i class="cal__marca" :style="{ background: color(e.idplantillas) }" aria-hidden="true"></i>
          <span class="cal__envio-titulo">{{ e.titulo }}</span>
        </button>
      </div>
    </div>

    <v-snackbar v-model="aviso.ver" timeout="2500" color="green darken-3" bottom>
      <span class="aviso">{{ aviso.texto }}</span>
    </v-snackbar>
    <Alerta v-if="respuestaAlerta" :parametros="parametros" />
  </section>
</template>

<script>
  import Alerta         from '@/components/alertas/Alerta.vue'
  import validarErrores from '@/mixins/validarErrores'

  const DIAS = ['L', 'M', 'M', 'J', 'V', 'S', 'D']
  const COLORES = ['#00A548', '#0E7C86', '#B26A00', '#6A4BB0', '#C0306A', '#1F6FB2', '#5E7D10']

  // Fechas locales en YYYY-MM-DD: toISOString() es UTC y de noche en México ya da el día siguiente.
  const aYmd = (d) => [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-')
  const deYmd = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d) }
  const sumarDias = (s, n) => { const d = deYmd(s); d.setDate(d.getDate() + n); return aYmd(d) }
  const lunesDe = (s) => sumarDias(s, -((deYmd(s).getDay() + 6) % 7))
  const diasEntre = (a, b) => {
    const [ya, ma, da] = a.split('-').map(Number)
    const [yb, mb, db] = b.split('-').map(Number)
    return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / 86400000)
  }

  export default {
    name: 'PlantillasCalendario',

    components: { Alerta },

    mixins: [ validarErrores ],

    props: {
      escuela: { type: Number, required: true },
      version: { type: Number, default: 0 },
    },

    data: () => ({
      DIAS,
      hoy: aYmd(new Date()),
      modo: 'ciclo',
      ciclo: null,
      cicloError: '',
      mes: aYmd(new Date()).slice(0, 7),
      dia: aYmd(new Date()),
      items: [],
      plantillas: [],
      idplantilla: null,
      cargando: false,
      guardando: false,
      aviso: { ver: false, texto: '' },
    }),

    computed: {
      rango () {
        if (this.modo === 'ciclo' && this.ciclo) return { desde: this.ciclo.inicio, hasta: this.ciclo.fin }
        const [y, m] = this.mes.split('-').map(Number)
        return { desde: `${this.mes}-01`, hasta: aYmd(new Date(y, m, 0)) }
      },

      semanas () {
        const { desde, hasta } = this.rango
        const porDia = {}
        this.items.forEach((e) => { (porDia[e.fecha] = porDia[e.fecha] || []).push(e) })

        const semanas = []
        for (let inicio = lunesDe(desde); inicio <= hasta; inicio = sumarDias(inicio, 7)) {
          const dias = []
          for (let i = 0; i < 7; i++) {
            const fecha = sumarDias(inicio, i)
            dias.push({
              fecha,
              num: deYmd(fecha).getDate(),
              enRango: fecha >= desde && fecha <= hasta,
              envios: porDia[fecha] || [],
            })
          }
          const n = this.semanaDe(dias.find((d) => d.enRango).fecha)
          semanas.push({ inicio, etiqueta: n ? 'S' + n : '', dias })
        }
        return semanas
      },

      enviosDelDia () {
        return this.items.filter((e) => e.fecha === this.dia)
      },

      disponibles () {
        const usadas = this.enviosDelDia.map((e) => e.idplantillas)
        return this.plantillas.filter((p) => !usadas.includes(p.idplantillas))
      },

      resumen () {
        return this.semanas
          .map((s) => ({
            inicio: s.inicio,
            titulo: s.etiqueta ? 'Semana ' + s.etiqueta.slice(1) : 'Semana del ' + this.corta(s.inicio),
            envios: s.dias.filter((d) => d.enRango).flatMap((d) => d.envios),
          }))
          .filter((g) => g.envios.length)
      },

      mesTexto () {
        return deYmd(`${this.mes}-01`).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })
      },
    },

    watch: {
      rango: { handler: 'cargarAgenda', deep: true },
      version () {
        this.cargarPlantillas()
        this.cargarAgenda()
      },
    },

    created () {
      this.cargarPlantillas()
      this.cargarCiclo()
    },

    methods: {
      semanaDe (fecha) {
        if (!this.ciclo || !fecha || fecha < this.ciclo.inicio || fecha > this.ciclo.fin) return null
        return Math.floor(diasEntre(this.ciclo.inicio, fecha) / 7) + 1
      },

      color (id) {
        return COLORES[Number(id) % COLORES.length]
      },

      corta (fecha, conDia) {
        const opts = conDia ? { weekday: 'short', day: 'numeric' } : { day: 'numeric', month: 'short' }
        return deYmd(fecha).toLocaleDateString('es-MX', opts)
      },

      larga (fecha) {
        return deYmd(fecha).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })
      },

      mayuscula (texto) {
        return texto.charAt(0).toUpperCase() + texto.slice(1)
      },

      etiquetaCelda (celda) {
        const n = celda.envios.length
        return this.larga(celda.fecha) + (n ? `, ${n} envío${n === 1 ? '' : 's'}` : ', sin envíos')
      },

      cambiarModo (modo) {
        this.modo = modo
        if (modo === 'mes') this.mes = this.dia.slice(0, 7)
        else if (this.ciclo && (this.dia < this.ciclo.inicio || this.dia > this.ciclo.fin)) this.dia = this.ciclo.inicio
      },

      moverMes (paso) {
        const [y, m] = this.mes.split('-').map(Number)
        this.mes = aYmd(new Date(y, m - 1 + paso, 1)).slice(0, 7)
        this.dia = this.mes === this.hoy.slice(0, 7) ? this.hoy : `${this.mes}-01`
      },

      cargarCiclo () {
        return this.$http.get('twilio.plantillas.catalogo.ciclo', { params: { escuela: this.escuela } }).then(({ data }) => {
          if (!data.fecha_fin_ciclo) throw new Error('El ciclo actual no tiene fecha de fin')
          this.ciclo = { nombre: data.ciclo, inicio: data.fecha_inicio_ciclo, fin: data.fecha_fin_ciclo }
          if (this.dia < this.ciclo.inicio || this.dia > this.ciclo.fin) this.dia = this.ciclo.inicio
        }).catch((error) => {
          this.cicloError = (error.response && error.response.data && error.response.data.message) || error.message
          this.modo = 'mes'
        })
      },

      cargarPlantillas () {
        return this.$http.get('twilio.plantillas.catalogo', { params: { escuela: this.escuela } }).then(({ data }) => {
          this.plantillas = data.filter((p) => p.activo)
        }).catch((error) => {
          this.validarError(error.response ? error.response.data.message : error.message)
        })
      },

      cargarAgenda () {
        const primera = this.semanas[0]
        const ultima = this.semanas[this.semanas.length - 1]
        this.cargando = true
        return this.$http.get('twilio.plantillas.agenda', {
          params: { escuela: this.escuela, desde: primera.inicio, hasta: sumarDias(ultima.inicio, 6) },
        }).then(({ data }) => {
          this.items = data
        }).catch((error) => {
          this.validarError(error.response ? error.response.data.message : error.message)
        }).finally(() => { this.cargando = false })
      },

      agendar () {
        const plantilla = this.plantillas.find((p) => p.idplantillas === this.idplantilla)
        this.guardando = true
        return this.$http.post('twilio.plantillas.agenda', { idplantillas: this.idplantilla, fecha: this.dia }).then(() => {
          this.idplantilla = null
          this.avisar(`«${plantilla ? plantilla.titulo : 'Plantilla'}» agendada el ${this.larga(this.dia)}`)
          return this.cargarAgenda()
        }).catch((error) => {
          this.validarError(error.response ? error.response.data.message : error.message)
        }).finally(() => { this.guardando = false })
      },

      quitar (envio) {
        this.guardando = true
        return this.$http.delete('twilio.plantillas.agenda/' + envio.idagenda).then(() => {
          this.avisar(`Se quitó «${envio.titulo}» de este día`)
          return this.cargarAgenda()
        }).catch((error) => {
          this.validarError(error.response ? error.response.data.message : error.message)
        }).finally(() => { this.guardando = false })
      },

      avisar (texto) {
        this.aviso = { ver: true, texto }
      },
    },
  }
</script>

<style scoped>
  .cal {
    --verde: #00A548;
    --verde-suave: #e6f6ed;
    --texto: #111b21;
    --tenue: #5b6b75;
    --linea: #e3e6e8;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(17, 27, 33, 0.08);
    padding: 14px 14px 16px;
    color: var(--texto);
  }

  @media (min-width: 960px) {
    .cal {
      height: 100%;
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: #c7cdd1 transparent;
    }
  }

  .cal__cabecera {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .cal__titulo {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.01em;
    margin: 0;
  }

  .cal__modo {
    display: inline-flex;
    background: #f1f3f4;
    border-radius: 8px;
    padding: 2px;
  }

  .cal__modo-btn {
    font-size: 12px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 6px;
    color: var(--tenue);
    transition: background-color 0.15s ease-out, color 0.15s ease-out;
  }

  .cal__modo-btn.is-activo {
    background: #fff;
    color: var(--texto);
    box-shadow: 0 1px 2px rgba(17, 27, 33, 0.12);
  }

  .cal__modo-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .cal__rango {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-height: 32px;
    margin: 10px 0 4px;
  }

  .cal__rango-nombre {
    font-size: 13px;
    font-weight: 600;
  }

  .cal__rango-nombre--mes {
    flex: 1;
    text-align: center;
    text-transform: capitalize;
    align-self: center;
  }

  .cal__rango-fechas {
    font-size: 12px;
    color: var(--tenue);
    font-variant-numeric: tabular-nums;
  }

  .cal__grid {
    margin-top: 6px;
  }

  .cal__fila {
    display: grid;
    grid-template-columns: 26px repeat(7, minmax(0, 1fr));
    gap: 2px;
    margin-bottom: 2px;
  }

  .cal__dia-nombre {
    text-align: center;
    font-size: 11px;
    font-weight: 600;
    color: var(--tenue);
    padding: 2px 0 4px;
  }

  .cal__semana {
    font-size: 10px;
    font-weight: 700;
    color: var(--verde);
    display: flex;
    align-items: center;
    font-variant-numeric: tabular-nums;
  }

  .cal__celda {
    position: relative;
    aspect-ratio: 1 / 1;
    min-height: 34px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
    color: var(--texto);
    transition: background-color 0.15s ease-out;
  }

  .cal__celda:hover:not(:disabled) {
    background: #f1f3f4;
  }

  .cal__celda:focus-visible,
  .cal__modo-btn:focus-visible,
  .cal__resumen-fila:focus-visible {
    outline: 2px solid var(--verde);
    outline-offset: 1px;
  }

  .cal__celda.is-fuera {
    color: #a3adb3;
  }

  .cal__celda:disabled {
    cursor: default;
  }

  .cal__celda.is-hoy .cal__num {
    box-shadow: inset 0 0 0 1.5px var(--verde);
    border-radius: 50%;
  }

  .cal__celda.is-elegido,
  .cal__celda.is-elegido:hover {
    background: var(--verde);
    color: #fff;
  }

  .cal__celda.is-elegido .cal__num {
    box-shadow: none;
  }

  .cal__celda.is-elegido .cal__puntos i {
    box-shadow: 0 0 0 1.5px #fff;
  }

  .cal__num {
    width: 24px;
    height: 24px;
    line-height: 24px;
    text-align: center;
  }

  .cal__puntos {
    display: flex;
    align-items: center;
    gap: 2px;
    height: 6px;
  }

  .cal__puntos i {
    width: 5px;
    height: 5px;
    border-radius: 50%;
  }

  .cal__puntos b {
    font-size: 9px;
    line-height: 1;
    font-weight: 700;
  }

  .cal__detalle {
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid var(--linea);
  }

  .cal__detalle-fecha {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .cal__detalle-semana {
    font-size: 11px;
    font-weight: 700;
    color: #00753a;
    background: var(--verde-suave);
    border-radius: 9px;
    padding: 1px 8px;
  }

  .cal__lista {
    list-style: none;
    padding: 0;
    margin: 0 0 10px;
  }

  .cal__envio {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    font-size: 13px;
  }

  .cal__marca {
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .cal__envio-titulo {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }

  .cal__vacio {
    font-size: 13px;
    color: var(--tenue);
    margin: 0 0 10px;
  }

  .cal__agregar {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .cal__agregar .v-autocomplete {
    flex: 1;
    min-width: 0;
  }

  .cal__resumen {
    margin-top: 18px;
    padding-top: 12px;
    border-top: 1px solid var(--linea);
  }

  .cal__resumen-titulo {
    font-size: 13px;
    font-weight: 700;
    margin: 0 0 8px;
  }

  .cal__grupo + .cal__grupo {
    margin-top: 10px;
  }

  .cal__grupo-titulo {
    font-size: 11px;
    font-weight: 700;
    color: var(--tenue);
    margin-bottom: 2px;
  }

  .cal__resumen-fila {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 5px 6px;
    margin: 0 -6px;
    border-radius: 6px;
    font-size: 13px;
    color: var(--texto);
    transition: background-color 0.15s ease-out;
  }

  .cal__resumen-fila:hover,
  .cal__resumen-fila.is-elegido {
    background: var(--verde-suave);
  }

  /* Varias vistas dejan `.v-snack__content { padding: 0 !important }` global; aquí se recupera. */
  .cal >>> .v-snack__content {
    padding: 14px 20px !important;
    margin-right: 0;
  }

  .cal >>> .v-snack__wrapper {
    min-width: 0;
  }

  .cal >>> .v-snack__action {
    display: none;
  }

  .aviso {
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 1.4;
  }

  .cal__resumen-fecha {
    flex: 0 0 52px;
    text-align: left;
    color: var(--tenue);
    font-variant-numeric: tabular-nums;
  }
</style>
